import React, { useEffect } from "react";
import { Refine } from "@refinedev/core";
import routerProvider, {
  CatchAllNavigate,
  NavigateToResource,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router-v6";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useNavigate, Route, Routes } from "react-router-dom";
import { Center, Loader } from "@mantine/core";

import { useAuth } from "../context/AuthContext";
import { adminDataProvider } from "./dataProvider";
import { refineAuthProvider } from "./refineAuthProvider";
import { AdminLayout } from "./AdminLayout";

import { RestaurantList } from "./pages/restaurants/RestaurantList";
import { RestaurantCreate } from "./pages/restaurants/RestaurantCreate";
import { RestaurantEdit } from "./pages/restaurants/RestaurantEdit";
import { RestaurantShow } from "./pages/restaurants/RestaurantShow";

import { MenuList } from "./pages/menus/MenuList";
import { MenuEdit } from "./pages/menus/MenuEdit";
import { MenuEditV2 } from "./pages/menus/v2/MenuEditV2";
import { MenuTesting } from "./pages/menus/MenuTesting";
import { MenuItemQATesting } from "./pages/MenuItemQATesting";

// Import admin upload pages
import { AdminV2Upload } from "./pages/upload/AdminV2Upload";
import { AdminUploadCSV } from "./pages/upload/AdminUploadCSV";
import { AdminImportCSV } from "./pages/upload/AdminImportCSV";

// Import ingredient pages
import { IngredientList } from "./pages/ingredients/IngredientList";
import { IngredientCreate } from "./pages/ingredients/IngredientCreate";
import { IngredientEdit } from "./pages/ingredients/IngredientEdit";

// Import dashboard and query history
import { Dashboard } from "./pages/Dashboard";
import { QueryHistory } from "./pages/QueryHistory";

const queryClient = new QueryClient();

/**
 * Refine-powered Admin panel.
 *
 * Mounted at `/admin/*` so the existing user-facing app keeps working.
 */
export default function AdminApp() {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  
  // Note: ColorSchemeManager in App.tsx handles all color scheme management
  // No need to set color scheme here - it would cause conflicts and flashing

  // Navigate to login if not authenticated or not admin
  // Using useEffect ensures navigation happens after the router is ready
  useEffect(() => {
    if (!loading && (!isAuthenticated || !isAdmin)) {
      navigate("/login", { replace: true });
    }
  }, [loading, isAuthenticated, isAdmin, navigate]);

  // Keep UX consistent with the rest of the app while auth initializes.
  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  // Extra guard: even if someone hits /admin directly, they must be an admin.
  // Return null while navigation is happening
  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Refine
        routerProvider={routerProvider}
        dataProvider={adminDataProvider}
        authProvider={refineAuthProvider}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          disableTelemetry: true,
        }}
        resources={[
          {
            name: "restaurants",
            list: "/admin/restaurants",
            create: "/admin/restaurants/create",
            edit: "/admin/restaurants/edit/:id",
            show: "/admin/restaurants/show/:id",
            meta: {
              label: "Restaurants",
            },
          },
          {
            name: "menus",
            list: "/admin/menus",
            edit: "/admin/menus/edit/v2/:id",
            meta: {
              label: "Menus",
            },
          },
          {
            name: "ingredients",
            list: "/admin/ingredients",
            create: "/admin/ingredients/create",
            edit: "/admin/ingredients/edit/:id",
            meta: {
              label: "Ingredients",
            },
          },
        ]}
      >
        <DocumentTitleHandler
          handler={({ resource, action }) => {
            const resourceLabel = resource?.meta?.label ?? resource?.name ?? "";
            if (action === "edit") return `Edit ${resourceLabel}`;
            if (action === "show") return `${resourceLabel}`;
            if (action === "create") return `New ${resourceLabel}`;
            if (action === "list") return resourceLabel;
            return resourceLabel;
          }}
        />
        <UnsavedChangesNotifier />

        <AdminLayout>
          <Routes>
            {/* Default admin landing - empty path matches /admin */}
            <Route path="" element={<Dashboard />} />

            {/* Restaurants - relative paths since we're already at /admin/* */}
            {/* More specific routes first to ensure proper matching */}
            <Route
              path="restaurants/create"
              element={<RestaurantCreate />}
            />
            <Route
              path="restaurants/edit/:id"
              element={<RestaurantEdit />}
            />
            <Route
              path="restaurants/show/:id"
              element={<RestaurantShow />}
            />
            <Route path="restaurants" element={<RestaurantList />} />

            {/* Menus */}
            <Route path="menus" element={<MenuList />} />
            <Route path="menus/edit/:id" element={<MenuEdit />} />
            <Route path="menus/edit/v2/:id" element={<MenuEditV2 />} />
            <Route path="menus/testing" element={<MenuTesting />} />

            {/* Ingredients */}
            <Route path="ingredients" element={<IngredientList />} />
            <Route path="ingredients/create" element={<IngredientCreate />} />
            <Route path="ingredients/edit/:id" element={<IngredientEdit />} />

            {/* Menu Item FAQ testing - restaurant/menu dropdown, generate FAQ for first 5 items, CSV download */}
            <Route path="menu-item-qa" element={<MenuItemQATesting />} />

            {/* Query history */}
            <Route path="query-history" element={<QueryHistory />} />

            {/* Upload pages */}
            <Route path="upload/v2" element={<AdminV2Upload />} />
            <Route path="upload/csv" element={<AdminUploadCSV />} />
            <Route path="upload/import-csv" element={<AdminImportCSV />} />

            {/* Catch-all within admin - redirect to restaurants list */}
            <Route path="*" element={<NavigateToResource resource="restaurants" />} />
          </Routes>
        </AdminLayout>
      </Refine>
    </QueryClientProvider>
  );
}
